<template>
  <BModal
    :model-value="show"
    title="収集カレンダー基本曜日設定"
    size="lg"
    ok-title="OK"
    cancel-title="キャンセル"
    :ok-disabled="loading"
    @ok="handleApply"
    @hide="handleClose"
  >
    <!-- 収集地区・年度選択 -->
    <div class="mb-3">
      <div class="row align-items-center g-3">
        <div class="col-auto">
          <span class="fw-bold me-2">収集地区:</span>
          <span>{{ district ? district.districtName : '' }}</span>
        </div>
        <div class="col-auto">
          <div class="input-group input-group-sm">
            <button class="btn btn-outline-secondary" @click="changeYear(-1)">&lt;</button>
            <span class="input-group-text bg-white fw-bold">{{ year }}年度</span>
            <button class="btn btn-outline-secondary" @click="changeYear(1)">&gt;</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ローディング -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- ごみ種別テーブル -->
    <div v-else class="table-responsive">
      <table class="table table-bordered table-hover align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th style="width: 30%">ごみ種別</th>
            <th>基本曜日</th>
            <th style="width: 100px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="wasteType in wasteTypes" :key="wasteType.id">
            <td>{{ wasteType.typeName }}</td>
            <td>{{ getPatternSummary(wasteType.id) }}</td>
            <td class="text-center">
              <button class="btn btn-sm btn-outline-primary" @click="openDetail(wasteType)">
                設定
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </BModal>

  <!-- 詳細設定モーダル -->
  <PatternDetailEditModal
    :show="showDetailModal"
    :district-name="district ? district.districtName : ''"
    :waste-type-name="selectedWasteType ? selectedWasteType.typeName : ''"
    :initial-pattern="selectedPattern"
    @close="showDetailModal = false"
    @save="handleDetailSave"
  />
  <ToastMessage ref="toastRef" />
</template>

<script setup>
import { ref, watch } from 'vue'
import { BModal } from 'bootstrap-vue-next'
import PatternDetailEditModal from './PatternDetailEditModal.vue'
import ToastMessage from '@/components/molecules/ToastMessage.vue'
import collectionPatternService from '@/services/systemSettings/collectionPatternService.js'

const props = defineProps({
  show: Boolean,
  district: Object,
  wasteTypes: Array,
  initialYear: Number
})

const emit = defineEmits(['close', 'applied'])

const year = ref(new Date().getFullYear())
const patterns = ref([])
const loading = ref(false)
const toastRef = ref(null)

const showDetailModal = ref(false)
const selectedWasteType = ref(null)
const selectedPattern = ref(null)

watch(() => props.show, (newVal) => {
  if (newVal) {
    year.value = props.initialYear || new Date().getFullYear()
    fetchPatterns()
  }
})

const fetchPatterns = async () => {
  if (!props.district) return

  loading.value = true
  try {
    const response = await collectionPatternService.getCollectionPatterns(props.district.id, year.value)
    if (response.success) {
      patterns.value = response.data
    }
  } catch (error) {
    console.error('パターンの取得に失敗しました', error)
  } finally {
    loading.value = false
  }
}

const changeYear = (diff) => {
  year.value += diff
  fetchPatterns()
}

const getPatternSummary = (wasteTypeId) => {
  const pattern = patterns.value.find(p => p.wasteTypeId === wasteTypeId)
  if (!pattern) return ''

  const days = ['日', '月', '火', '水', '木', '金', '土']

  if (pattern.patternType === 'weekly') {
    const dayNames = pattern.settings.daysOfWeek.map(d => days[d])
    return `毎週 ${dayNames.join(' ')}`
  } else if (pattern.patternType === 'specific-week') {
    return pattern.settings.patterns.map(p => {
      return p.weeks.map(w => `第${w}${days[p.dayOfWeek]}`).join(' ')
    }).join(' ')
  } else if (pattern.patternType === 'biweekly') {
    const dayNames = pattern.settings.daysOfWeek.map(d => days[d])
    const start = pattern.settings.startPattern === 'first-week' ? '第1週開始' : '第2週開始'
    return `隔週 ${dayNames.join(' ')} (${start})`
  }
  return ''
}

const openDetail = (wasteType) => {
  selectedWasteType.value = wasteType
  selectedPattern.value = patterns.value.find(p => p.wasteTypeId === wasteType.id) || null
  showDetailModal.value = true
}

const handleDetailSave = async ({ patternType, settings }) => {
  try {
    const patternData = {
      districtId: props.district.id,
      year: year.value,
      wasteTypeId: selectedWasteType.value.id,
      patternType,
      settings,
      isActive: true
    }

    let response
    if (selectedPattern.value) {
      response = await collectionPatternService.updateCollectionPattern(selectedPattern.value.id, patternData)
    } else {
      response = await collectionPatternService.createCollectionPattern(patternData)
    }

    if (response.success) {
      showDetailModal.value = false
      fetchPatterns()
    }
  } catch (error) {
    console.error('パターンの保存に失敗しました', error)
    toastRef.value?.error('保存に失敗しました')
  }
}

const handleApply = async () => {
  if (!confirm('設定内容でカレンダーを上書きしますがよろしいですか？\n※日単位で設定済みのデータも上書きされます。')) {
    return
  }

  loading.value = true
  try {
    const response = await collectionPatternService.applyPatternsToCalendar({
      districtId: props.district.id,
      year: year.value,
      overwriteExisting: true
    })

    if (response.success) {
      toastRef.value?.success('カレンダーに適用しました')
      emit('applied')
      emit('close')
    }
  } catch (error) {
    console.error('適用に失敗しました', error)
    toastRef.value?.error('適用に失敗しました')
  } finally {
    loading.value = false
  }
}

const handleClose = () => {
  emit('close')
}
</script>
